import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import InnerLayout from "../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/tables/dynamic-table1";
import { DataContext } from "../../components/layouts/main-layout";
import { useHotkeys } from "../../components/helper-components/use-hotkeys";
import EditIcon, { VoidIcon } from "../../assets/icons/action-icons";
import EyeOpenIcon from "../../assets/icons/eye-open-icon";
import KibabMenu from "../../assets/icons/kibab-menu";
import { userCreationListApi } from "../../redux/apis/user-api";

const ActionCell = ({ id, onView, onEdit, onDeactivate, onVoid }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex items-center gap-3 justify-center w-full" onClick={(e) => e.stopPropagation()}>
      <button title="View" onClick={() => onView(id)} className="flex items-center hover:opacity-70">
        <EyeOpenIcon fill="#98A2B3" />
      </button>
      <button title="Edit" onClick={() => onEdit(id)} className="flex items-center hover:opacity-70">
        <EditIcon fill="#98A2B3" />
      </button>
      <div ref={ref} className="relative">
        <button title="More" onClick={() => setOpen((o) => !o)} className="flex items-center hover:opacity-70">
          <KibabMenu />
        </button>
        {open && (
          <div
            style={{ minWidth: "11rem", zIndex: 999, top: "25px", right: "15px", boxShadow: "0px 1px 3px 0px var(--popover-shadow-color)" }}
            className="absolute border border-[var(--popover-border-color)] py-2 px-2 bg-[var(--popover-bg-color)] rounded flex flex-col"
          >
            <button
              onClick={() => { onDeactivate(id); setOpen(false); }}
              className="flex items-center gap-2 text-[length:--popover-font-size] font-[var(--popover-font-weight)] text-[var(--popover-fg-color)] whitespace-nowrap cursor-pointer h-[35px] hover:bg-[var(--popover-hover-color)] rounded px-[8px]"
            >
              Deactivate
            </button>
            <button
              onClick={() => { onVoid(id); setOpen(false); }}
              className="flex items-center gap-2 text-[length:--popover-font-size] font-[var(--popover-font-weight)] text-[var(--error-color)] whitespace-nowrap cursor-pointer h-[35px] hover:bg-[var(--popover-hover-color)] rounded px-[8px]"
            >
              <VoidIcon fill="var(--error-color)" />
              Void User
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const UserCreation = () => {
  const { permissionFn } = useContext(DataContext);
  const navigate = useNavigate();

  const [userList, setUserList] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "",
    inputValue: "",
  });

  useEffect(() => {
    setLoading(true);
    userCreationListApi({ pageNumber: currentPage, pageSize, filterData })
      .then((res) => {
        const raw = res.data?.data ?? res.data;
        setUserList(Array.isArray(raw) ? raw : raw?.list ?? raw?.userList ?? []);
        setDataCount(res.data?.data?.totalCount ?? res.data?.totalCount ?? userList.length);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [currentPage, pageSize, filterData, reload]);

  const tableBodyData = userList.map((item, index) => ({
    id: item?.user_id ?? item?.id ?? item?.store_app_use_phone_number ?? index,
    user_name: <span className="fw_500">{item?.user_name ?? "--"}</span>,
    user_phone_number: item?.store_app_use_phone_number ?? "--",
    row_actions: (
      <ActionCell
        id={item?.store_app_user_id ?? item?.user_id ?? index}
        onView={(id) => navigate(`/view-user-creation/${id}`)}
        onEdit={(id) => navigate(`/edit-user-creation/${id}`)}
        onDeactivate={() => {}}
        onVoid={() => {}}
      />
    ),
  }));

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currentPage,
  };

  const handleFilterClick = useCallback(() => setShowFilter((prev) => !prev), []);
  useHotkeys("escape", () => setShowFilter(false));

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="store_execution_process"
      list={true}
      onclkFn={() => navigate("/add-user-creation")}
      title="User Creation"
    >
      <DynamicTable
        is_checkbox_req={false}
        onClickItemFn={() => {}}
        list={true}
        onclkFn={() => navigate("/add-user-creation")}
        permissionFn={permissionFn}
        module="store_execution_process"
        menu={false}
        date={false}
        path="user-creation"
        id_name="user_id"
        reload={reload}
        setReload={setReload}
        loading={loading}
        setLoading={setLoading}
        label="User"
        showFilter={showFilter}
        filterFn={handleFilterClick}
        setFilterData={setFilterData}
        filterData={filterData}
        paginationProps={paginationProps}
        tableHeading={tableHeading}
        tableBodyData={tableBodyData}
        placeholder="Search by User Name or Phone Number"
        initialFilter={{
          sort_by: "",
          sort_order: "",
          selectColumn: "",
          inputValue: "",
        }}
      />
    </InnerLayout>
  );
};

export default UserCreation;

const tableHeading = [
  { label: "USER NAME", value: "user_name", display: true, column: true, isColumnneeded: true },
  { label: "PHONE NUMBER", value: "user_phone_number", display: true, column: true, isColumnneeded: true },
  { label: "ACTION", value: "row_actions", display: true, column: false, isColumnneeded: true },
];